import { useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Minus, Plus, Copy, Mail, Download } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import { useSite } from "../site/SiteContext";
import { useCatalogue, displayName } from "../site/useCatalogue";
import ProductCard from "../site/ProductCard";
import QuickView from "../site/QuickView";
import { useSEO, waLink, EMAIL, CATALOGUE_URL } from "../seo";
import { reducedMotion } from "../site/fx";

export default function ProductDetail() {
  const { code } = useParams();
  const { products } = useCatalogue();
  const { add, fly } = useSite();
  const [qty, setQty] = useState(100);
  const [vi, setVi] = useState(0);
  const [quick, setQuick] = useState(null);
  const imgRef = useRef(null);
  const zoomRef = useRef(null);

  const product = useMemo(() => {
    let p = products.find((x) => x.code === code);
    let variantIdx = 0;
    if (!p) {
      products.forEach((x) => (x.variants || []).forEach((v, i) => { if (v.code === code) { p = x; variantIdx = i; } }));
    }
    return { p, variantIdx };
  }, [products, code]);

  const p = product.p;
  useSEO(
    p ? `${p.name} (${p.code}) — Sepla Hotelware` : "Product — Sepla Hotelware",
    p ? p.description : ""
  );

  if (!p) return <main className="page page-pad"><p className="pempty">Product not found. <Link to="/products">Browse the range</Link>.</p></main>;

  const activeVi = p.variants?.length ? (product.variantIdx || vi) : 0;
  const variant = p.variants?.[activeVi];
  const activeCode = variant?.code || p.code;
  const related = products.filter((x) => x.category === p.category && x.code !== p.code).slice(0, 4);

  const onZoom = (e) => {
    const el = zoomRef.current;
    if (!el || reducedMotion()) return;
    const r = el.getBoundingClientRect();
    el.style.transformOrigin = `${((e.clientX - r.left) / r.width) * 100}% ${((e.clientY - r.top) / r.height) * 100}%`;
  };

  const copyCode = () => {
    navigator.clipboard?.writeText(activeCode);
    toast.success("Copied", { description: activeCode });
  };

  const waMsg = `Hi Sepla, please share a quotation for ${displayName(p.name)} (${activeCode}), quantity ${qty}.`;

  return (
    <main className="page" data-testid="product-detail-page">
      <div className="pd">
        <div className="pd-imgwrap" data-testid="product-image-zoom">
          <div ref={zoomRef} className="pd-zoom" onMouseMove={onZoom}>
            <img ref={imgRef} src={p.image} alt={p.name} />
          </div>
        </div>
        <div className="pd-info">
          <p className="kicker"><Link to={`/category/${p.category}`}>Back to category</Link></p>
          <h1 className="pd-name" data-testid="product-name">{displayName(p.name)}{variant ? ` · ${variant.size}` : ""}</h1>
          <div className="pd-coderow">
            <span className="pd-code" data-testid="product-code">{activeCode}</span>
            <button className="copy-btn" onClick={copyCode} aria-label="Copy product code" data-testid="copy-code"><Copy size={13} /> Copy</button>
          </div>
          <p className="pd-desc">{p.description}</p>

          {p.variants?.length > 1 && (
            <div className="pcard-variants pd-variants" data-testid="detail-size-toggle">
              {p.variants.map((v, i) => (
                <button key={v.code} className={i === activeVi ? "on" : ""} onClick={() => setVi(i)}>{v.size}</button>
              ))}
            </div>
          )}

          <table className="spec-table" data-testid="spec-table">
            <tbody>
              <tr><td>Product code</td><td>{activeCode}</td></tr>
              {p.configuration && <tr><td>Configuration</td><td>{p.configuration}</td></tr>}
              {(variant?.size || p.capacity) && <tr><td>Capacity</td><td>{variant?.size || p.capacity}</td></tr>}
              <tr><td>Material</td><td>{p.material}</td></tr>
              <tr><td>Finish</td><td>{p.finish}</td></tr>
              <tr><td>Dimensions</td><td>Shared with quotation</td></tr>
            </tbody>
          </table>

          <div className="pd-actions">
            <div className="pd-qty" data-testid="quantity-selector">
              <button onClick={() => setQty(Math.max(1, qty - 50))} aria-label="Decrease quantity" data-testid="qty-minus"><Minus size={14} /></button>
              <b>{qty}</b>
              <button onClick={() => setQty(qty + 50)} aria-label="Increase quantity" data-testid="qty-plus"><Plus size={14} /></button>
            </div>
            <button className="btn btn-accent" onClick={() => { add(p, qty, variant?.code); fly(imgRef.current, p.image); }} data-testid="detail-add-enquiry">
              <Plus size={14} /> Add to enquiry
            </button>
          </div>
          <div className="pd-actions">
            <a className="btn btn-whatsapp" href={waLink(waMsg)} target="_blank" rel="noreferrer" data-testid="detail-whatsapp"><FaWhatsapp size={15} /> Get a quote on WhatsApp</a>
            <a className="btn btn-ghost" href={`mailto:${EMAIL}?subject=${encodeURIComponent(`Enquiry: ${p.name} (${activeCode})`)}`} data-testid="detail-email"><Mail size={14} /> Email us</a>
            <a className="btn btn-ghost" href={CATALOGUE_URL} download data-testid="detail-download-catalogue"><Download size={14} /> Catalogue</a>
          </div>

          <div className="mto" data-testid="made-to-order">
            <p className="mto-title">Made to order</p>
            <p>Need a different size, finish or your own logo?</p>
            <Link to="/custom-oem" className="mto-link">Start a custom request →</Link>
          </div>
        </div>
      </div>

      {related.length > 0 && (
        <section className="sect sect-light">
          <div className="sect-in">
            <p className="kicker">Related</p>
            <h2 className="sect-title-sm">More from this category</h2>
            <div className="pgrid pgrid-4">
              {related.map((x) => <ProductCard key={x.code} product={x} onQuickView={setQuick} />)}
            </div>
          </div>
        </section>
      )}
      <QuickView product={quick} onClose={() => setQuick(null)} />
    </main>
  );
}
